import React, { useState } from "react";
import Layout, { Main } from "../Layout";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Alert,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import { formatDate } from "../utils/DateFormater";
import { useApi } from "../Api/ApiContext";
import { useAsync } from "react-use";
import useMeStore from "../store/MeStore";
import useCelebritiesStore from "../store/CelebritiesStore";
import {
  DATE_ACCESSOR,
  EmptyState,
  NUMBER_ACCESSOR,
  OrderStatus,
  SortableHeaders,
  STATUS_ACCESSOR,
  StyledTable,
  TableWrapper,
  TEXT_ACCESSOR,
  useTableSort,
} from "../component/DataTable";

const ORDER_COLUMNS = [
  { key: "createdAt", label: "Date" },
  { key: "type", label: "Type" },
  { key: "dedicaceFrom", label: "Demandeur" },
  { key: "dedicaceTo", label: "Bénéficiaire" },
  { key: "orderReason", label: "Raison" },
  { key: "deliveryStatus", label: "Statut" },
  { key: "price", label: "Prix" },
];

const ORDER_ACCESSORS = {
  createdAt: DATE_ACCESSOR("createdAt"),
  type: TEXT_ACCESSOR("type"),
  dedicaceFrom: TEXT_ACCESSOR("dedicaceFrom"),
  dedicaceTo: TEXT_ACCESSOR("dedicaceTo"),
  orderReason: TEXT_ACCESSOR("orderReason"),
  deliveryStatus: STATUS_ACCESSOR("deliveryStatus"),
  price: NUMBER_ACCESSOR("price"),
};

const PaymentOrders = ({ orders }) => {
  const {
    sortedRows: sortedOrders,
    sort,
    toggleSort,
  } = useTableSort(orders, ORDER_ACCESSORS);

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <SortableHeaders
            columns={ORDER_COLUMNS}
            sort={sort}
            onSort={toggleSort}
          />
        </thead>
        <tbody>
          {!sortedOrders || sortedOrders.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <EmptyState>Aucune commande liée à ce paiement.</EmptyState>
              </td>
            </tr>
          ) : (
            sortedOrders.map((order, index) => (
              <tr key={order.id || index}>
                <td data-label={"Date"}>
                  {formatDate(new Date(order.createdAt), "Y-MM-dd")}
                </td>
                <td data-label={"Type"}>{order.type}</td>
                <td data-label={"Demandeur"}>{order.dedicaceFrom}</td>
                <td data-label={"Bénéficiaire"}>{order.dedicaceTo}</td>
                <td data-label={"Raison"}>{order.orderReason}</td>
                <td data-label={"Statut"}>
                  <OrderStatus status={order.deliveryStatus} />
                </td>
                <td data-label={"Prix"}>{order.price}</td>
              </tr>
            ))
          )}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

const Payments = () => {
  const { getPayments } = useApi();
  const { me } = useMeStore();
  const {
    value: payments,
    error,
    loading,
  } = useAsync(() => getPayments(me.id));
  const [open, setOpen] = useState("1");
  const { celebrities } = useCelebritiesStore();

  const toggle = (id) => {
    if (open === id) {
      setOpen();
    } else {
      setOpen(id);
    }
  };

  if (loading) {
    return (
      <Layout celebrities={celebrities.items}>
        <Main>
          <Row className={"justify-content-center"}>
            <Col xs={2} className="mx-auto">
              <Spinner color={"danger"} />
            </Col>
          </Row>
        </Main>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
        <Main>
          <Alert color={"danger"}>
            <h1 className={"fs-4"}>Une erreur est survenue.</h1>
            <p>
              Nos équipes sont mobilisées pour rétablir le service dans les
              meilleurs délai.
            </p>
          </Alert>
        </Main>
      </Layout>
    );
  }

  return (
    <Layout backPath={{ pathname: "/admin" }} celebrities={celebrities.items}>
      <Main>
        <h1 className={"fs-1"}>Historique des paiements</h1>
        <Row>
          <Col>
            <Accordion className={"mt-4"} open={open} toggle={toggle}>
              {payments.map((payment, index) => (
                <AccordionItem key={index}>
                  <AccordionHeader targetId={payment.id}>
                    <Row className={"w-100 d-flex justify-content-between"}>
                      <Col md={6} className={"text-start"}>
                        <b>
                          Paiement du{" "}
                          {formatDate(new Date(payment.createdAt), "Y-MM-dd")}
                        </b>
                      </Col>
                      <Col md={6} className={"text-end"}>
                        {payment.amountPaid} {payment.currency}s
                      </Col>
                    </Row>
                  </AccordionHeader>
                  <AccordionBody accordionId={payment.id}>
                    <p>Référence : {payment.paymentRef}</p>
                    <p>Mode de paiement : {payment.mode}</p>
                    <p>
                      Montant total : {payment.totalAmount} {payment.currency}s
                    </p>
                    <p>
                      Montant payé: {payment.amountPaid} {payment.currency}s
                    </p>
                    <p>
                      Commission : {payment.charge} {payment.currency}s
                    </p>
                    <h3 className={"fs-5 mb-2"}>Détails</h3>
                    <PaymentOrders orders={payment.orders} />
                  </AccordionBody>
                </AccordionItem>
              ))}
            </Accordion>
          </Col>
        </Row>
      </Main>
    </Layout>
  );
};

export default Payments;
